import { Table } from '@radix-ui/themes';
import PropTypes from 'prop-types';
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useResizeDetector } from 'react-resize-detector';
import styled, { css } from 'styled-components';
import { overflowShadowShape } from './use-overflow-shadows';

const TableWrapper = styled.div`
  border: solid 1px var(--neutral-4);
  border-radius: 7px;

  .rt-ScrollAreaViewport {
    border-left: 1px solid transparent;
    border-right: 1px solid transparent;
    overscroll-behavior-x: none;
  }

  .rt-ScrollAreaScrollbar {
    z-index: 3;
  }

  th {
    background-color: var(--neutral-2);

    &:first-child {
      border-top-left-radius: calc(var(--table-border-radius) - 1px);
    }

    &:last-child {
      border-top-right-radius: calc(var(--table-border-radius) - 1px);
    }
  }

  td {
    background-color: var(--color-panel);
  }

  tbody tr:last-child td {
    &:first-child {
      border-bottom-left-radius: calc(var(--table-border-radius) - 1px);
    }

    &:last-child {
      border-bottom-right-radius: calc(var(--table-border-radius) - 1px);
    }
  }

  td,
  th {
    ${props =>
      props.overflowShadows?.stickyFirstColumn &&
      css`
        &:first-child {
          position: sticky;
          left: 0;
          z-index: 1;
        }
      `}

    ${props =>
      props.overflowShadows?.stickyLastColumn &&
      css`
        &:last-child {
          position: sticky;
          right: 0;
          z-index: 1;
        }
      `}
  }
`;

const StyledBaseOverflowShadow = styled.div`
  position: absolute;
  height: ${props => props.height}px;
  width: 8px;
  z-index: 2;
  opacity: ${props => props.opacity || 0};
`;

const StyledLeftOverflowShadow = styled(StyledBaseOverflowShadow)`
  left: ${props => props.left || 0}px;
  background: linear-gradient(90deg, var(--gray-a4) 0%, transparent 100%);
`;

const StyledRightOverflowShadow = styled(StyledBaseOverflowShadow)`
  right: ${props => props.right || 0}px;
  background: linear-gradient(270deg, var(--gray-a4) 0%, transparent 100%);
`;

const overflowShadowSideSpecificData = {
  left: {
    // Because of render order, LeftOverflowShadow is before RightOverflowShadow, which is before the table row.
    // So we need to get the next next sibling to get the table row.
    getDistanceToEdge: elementRef => {
      const rowElement = elementRef.current.nextElementSibling?.nextElementSibling;
      return rowElement?.children[0].offsetWidth;
    },
    Element: StyledLeftOverflowShadow,
  },
  right: {
    // We need to know the height of the table row which is the next sibling.
    getDistanceToEdge: elementRef => {
      const rowElement = elementRef.current.nextElementSibling;
      return rowElement?.children[rowElement.children.length - 1].offsetWidth - 1.5;
    },
    Element: StyledRightOverflowShadow,
  },
};

function OverflowShadow({ firstDataItemId, side, ...props }) {
  const elementRef = useRef(null);
  const [height, setHeight] = useState(0);
  const [distance, setDistance] = useState(0);

  const { getDistanceToEdge, Element } = overflowShadowSideSpecificData[side];

  const distanceObject = side === 'left' ? { left: distance } : { right: distance };

  useLayoutEffect(() => {
    if (!elementRef.current) return;

    const tableRoot = elementRef.current.closest('.onoTableRoot');
    if (!tableRoot) return;

    const update = () => {
      const parentElementHeight = tableRoot.offsetHeight;
      const distanceFromSide = getDistanceToEdge(elementRef);

      if (parentElementHeight && distanceFromSide) {
        setHeight(parentElementHeight);
        setDistance(distanceFromSide);
      }
    };

    update();

    const observer = new ResizeObserver(update);
    observer.observe(tableRoot);

    return () => observer.disconnect();
  }, [firstDataItemId, getDistanceToEdge]);

  return (
    <Element
      ref={elementRef}
      height={height}
      {...distanceObject}
      {...props}
    />
  );
}

OverflowShadow.propTypes = {
  opacity: PropTypes.number,
  firstDataItemId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  side: PropTypes.oneOf(['left', 'right']).isRequired,
};

function TableRoot({ children, overflowShadows }) {
  const { width, height, ref } = useResizeDetector();

  useEffect(() => {
    if (!overflowShadows?.stickyFirstColumn && !overflowShadows?.stickyLastColumn) return;
    if (ref.current && overflowShadows.onScroll) {
      const tableViewScrollArea = ref.current.querySelector('.rt-ScrollAreaViewport');
      const onScroll = () => {
        const scrollFromLeft = tableViewScrollArea.scrollLeft;
        const scrollFromRight =
          tableViewScrollArea.scrollWidth - tableViewScrollArea.clientWidth - tableViewScrollArea.scrollLeft;

        overflowShadows.onScroll({ scrollFromLeft, scrollFromRight });
      };

      tableViewScrollArea.addEventListener('scroll', onScroll);
      onScroll();
      return () => {
        tableViewScrollArea.removeEventListener('scroll', onScroll);
      };
    }
  }, [ref, width, height, overflowShadows]);

  return (
    <TableWrapper
      ref={ref}
      overflowShadows={overflowShadows}
    >
      <div className='onoTableRoot'>
        <Table.Root>{children}</Table.Root>
      </div>
    </TableWrapper>
  );
}

TableRoot.propTypes = {
  children: PropTypes.node.isRequired,
  overflowShadows: overflowShadowShape,
};

export function TableRowWithOverflowShadows({ overflowShadows, firstDataItemId, ...props }) {
  if (!overflowShadows) {
    return <Table.Row {...props} />;
  }
  return (
    <>
      <OverflowShadow
        side='left'
        opacity={overflowShadows?.styleInfo?.firstCell?.opacity}
        firstDataItemId={firstDataItemId}
      />
      <OverflowShadow
        side='right'
        opacity={overflowShadows?.styleInfo?.lastCell?.opacity}
        firstDataItemId={firstDataItemId}
      />
      <Table.Row {...props} />
    </>
  );
}

TableRowWithOverflowShadows.propTypes = {
  overflowShadows: overflowShadowShape,
  currentPage: PropTypes.number,
  firstDataItemId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

export { TableRoot };
